import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { workspaceLogin, fetchAuthMe } from '@/services/api';

const ERROR_MESSAGES: Record<string, string> = {
  workspace_config: 'D365 token authentication is not configured yet. Please contact the administrator.',
  workspace_invalid: 'AccessToken validation failed. Please get a fresh link from D365 and try again.',
  workspace_unauthorized: 'Your email is not in the allowed list for this app.',
  workspace_missing_email: 'The AccessToken was parsed, but no email was returned.',
  workspace_missing_token: 'AccessToken is required.',
  workspace_session_failed: 'Login succeeded but session creation failed. Please try again.',
};

const BROWSER_STEPS: Record<string, { title: string; items: string[] }> = {
  chrome: {
    title: '🌐 Chrome 浏览器',
    items: [
      '📌 快速方式：点击地址栏右侧 👁️ 小眼睛图标',
      '找到「Cookie」选项并设置为允许',
      '--- 或 ---',
      '点击右上角 ⋮ 三个点图标',
      '选择「设置」',
      '点击「隐私和安全」',
      '选择「第三方 Cookie」',
      '选择「允许所有第三方 Cookie」',
    ],
  },
  safari: {
    title: '🍎 Safari 浏览器',
    items: [
      '点击顶部「Safari」菜单 →「设置」',
      '切换到「隐私与安全性」标签',
      '在「Cookie 和网站数据」中选择「始终允许」',
    ],
  },
  edge: {
    title: '🔷 Edge 浏览器',
    items: [
      '📌 快速方式：点击地址栏右侧 👁️ 小眼睛图标',
      '找到「Cookie」选项并设置为允许',
      '--- 或 ---',
      '点击右上角 ⋯ 三个点图标',
      '选择「设置」',
      '点击「隐私、搜索和服务」',
      '选择「Cookie」',
      '关闭「阻止第三方 Cookie」开关',
    ],
  },
  firefox: {
    title: '🦊 Firefox 浏览器',
    items: [
      '点击地址栏左侧的 🔒 图标',
      '选择「更多信息」',
      '在「权限」选项中找到「Cookie」并允许',
    ],
  },
  other: {
    title: '🌐 其他浏览器',
    items: [
      '点击地址栏左侧的安全图标',
      '找到 Cookie 设置选项',
      '设置为允许第三方 Cookie',
    ],
  },
};

function getBrowser(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'safari';
  if (ua.includes('Edg')) return 'edge';
  if (ua.includes('Firefox')) return 'firefox';
  return 'other';
}

function isInIframe(): boolean {
  try { return window.self !== window.top; } catch { return true; }
}

function isThirdPartyCookieAllowed(): boolean {
  const name = 'third_party_test_' + Date.now();
  try {
    document.cookie = `${name}=1; path=/; SameSite=None; Secure`;
    const ok = document.cookie.includes(name);
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
    return ok;
  } catch { return false; }
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [showCookie, setShowCookie] = useState(false);
  const [loading, setLoading] = useState(false);
  const browser = getBrowser();

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam && ERROR_MESSAGES[errorParam]) {
      setError(ERROR_MESSAGES[errorParam]);
    }

    if (isInIframe() && !isThirdPartyCookieAllowed()) {
      setTimeout(() => setShowCookie(true), 500);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const form = e.target as HTMLFormElement;
    const tokenInput = form.elements.namedItem('workspace-token') as HTMLTextAreaElement;
    const accesstoken = tokenInput.value.trim();

    if (!accesstoken) {
      setError('AccessToken is required.');
      return;
    }

    setLoading(true);
    try {
      const data = await workspaceLogin({ accesstoken });
      if (data.success) {
        navigate('/', { replace: true });
      } else {
        setError(ERROR_MESSAGES[data.error || ''] || data.error || 'AccessToken login failed.');
      }
    } catch (err: any) {
      const serverMessage = err?.response?.data?.error;
      setError(ERROR_MESSAGES[serverMessage] || serverMessage || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const steps = BROWSER_STEPS[browser] || BROWSER_STEPS.other;

  return (
    <div style={{
      fontFamily: "'JohnsonText', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #fef2f2 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        width: 440, background: 'white', borderRadius: 12,
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '32px 36px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', flexDirection: 'column', color: '#eb1923', lineHeight: 1, marginBottom: 6 }}>
            <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: -0.3, marginBottom: 2 }}>Johnson&Johnson</span>
            <span style={{ fontSize: 22, fontWeight: 500, letterSpacing: -0.6 }}>MedTech</span>
          </div>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 8 }}>AI Highlight - War Room</div>
        </div>

        {/* Body */}
        <div style={{ padding: '28px 36px 36px' }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>D365 AccessToken Login</div>
          <div style={{ color: '#64748b', fontSize: 12, lineHeight: 1.6, marginBottom: 14 }}>
            通过 D365 跳转访问本页时，如果 URL 中带有 <code>accesstoken</code>，系统会自动完成登录并直接进入你有权限查看的页面。
          </div>

          {error && (
            <div style={{
              background: '#fef2f2', color: '#dc2626', padding: '10px 14px',
              borderRadius: 6, fontSize: 13, marginBottom: 14,
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#4a5568', marginBottom: 6 }}>
                AccessToken
              </label>
              <textarea
                name="workspace-token"
                placeholder="仅用于测试时手动粘贴 accesstoken"
                style={{
                  width: '100%', minHeight: 110, padding: '10px 14px',
                  border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13,
                  fontFamily: 'inherit', color: '#1a202c', background: '#f8fafc',
                  resize: 'vertical', outline: 'none',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#eb1923'; e.target.style.boxShadow = '0 0 0 3px rgba(235,25,35,0.1)'; e.target.style.background = 'white'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; e.target.style.background = '#f8fafc'; }}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '11px 16px', borderRadius: 6, fontSize: 14,
                fontWeight: 600, fontFamily: 'inherit', cursor: loading ? 'not-allowed' : 'pointer',
                background: loading ? '#fca5a5' : '#eb1923', color: 'white', border: 'none',
              }}
            >
              {loading ? 'Logging in...' : 'Login with AccessToken'}
            </button>
          </form>

          <div style={{
            marginTop: 16, padding: '12px 14px', borderRadius: 6,
            background: '#f8fafc', color: '#64748b', fontSize: 12, lineHeight: 1.6,
          }}>
            如果你是普通业务用户，不需要管理员账号密码。只有在 D365 token 无法自动带入时，才需要在这里手动粘贴 token 测试。
          </div>
        </div>
      </div>

      {/* Cookie modal */}
      {showCookie && (
        <div
          onClick={() => setShowCookie(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 420, background: 'white', borderRadius: 10, overflow: 'hidden',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{
              padding: '20px 24px', background: 'linear-gradient(135deg, #eb1923 0%, #c41018 100%)',
              color: 'white', display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <span style={{ fontSize: 20 }}>🔒</span>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Cookie 权限设置</h3>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ margin: '0 0 12px', color: '#4a5568', fontSize: 13, lineHeight: 1.6 }}>
                检测到您的浏览器可能阻止了第三方 Cookie，这会导致登录会话无法正常工作。
              </p>
              <p style={{ margin: '0 0 12px', color: '#4a5568', fontSize: 13, lineHeight: 1.6 }}>
                请按照以下步骤开启第三方 Cookie：
              </p>
              <div style={{
                background: '#f8fafc', borderRadius: 8, padding: 16, marginBottom: 16,
              }}>
                <h4 style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {steps.title}
                </h4>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#64748b', lineHeight: 2 }}>
                  {steps.items.map((item, i) => {
                    if (item.startsWith('---')) {
                      return <li key={i} style={{ textAlign: 'center' as const, fontWeight: 600, color: '#94a3b8', margin: '8px 0' }}>或</li>;
                    }
                    return <li key={i} dangerouslySetInnerHTML={{ __html: item.replace(/<code>/g, '<code style="color:#334155">') }} />;
                  })}
                </ul>
              </div>
              <p style={{ fontSize: 12, color: '#94a3b8', marginTop: -4 }}>
                设置完成后点击下方按钮刷新页面重试
              </p>
            </div>
            <div style={{
              padding: '16px 24px', borderTop: '1px solid #e2e8f0',
              display: 'flex', justifyContent: 'flex-end', gap: 10,
            }}>
              <button
                onClick={() => setShowCookie(false)}
                style={{
                  padding: '10px 20px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                  fontFamily: 'inherit', cursor: 'pointer', border: '1px solid #e2e8f0',
                  background: 'white', color: '#64748b',
                }}
              >
                稍后提醒
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '10px 20px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                  fontFamily: 'inherit', cursor: 'pointer', border: 'none',
                  background: '#eb1923', color: 'white',
                }}
              >
                刷新页面重试
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
